/**
 * What still blocks a registration step, in words a vendor can act on.
 *
 * Pure on purpose (no DOM, no React) so node:test pins the rules — see missingFields.test.js.
 * The page supplies the on-screen field order and the labels; this module decides what to say.
 */
import { fieldLabel } from './fieldLabels.js';

/** Verification gates. When only these block a step, the hint says what to do rather than listing names. */
export const GATE_HINTS = {
  countryOrigin: 'Select your country to continue',
  gstinVerified: 'Verify your GSTIN to continue',
  emailVerified: 'Verify your email to continue',
  bankVerified: 'Verify your bank account to continue',
  udyamVerified: 'Look up your Udyam number to continue',
  finalConfirmation: 'Tick the confirmation to submit',
};

/**
 * Inputs a pending gate keeps off the screen. Until the gate clears they cannot be typed into (the
 * verification fills most of them), so naming them would point the vendor at nothing.
 *  - Identity: the email code panel only appears once a country is picked.
 *  - Bank: with verification on, the typed inputs and the proof upload appear after verifying
 *    (or after acknowledging an outage).
 *  - MSME: a registered vendor's Udyam details appear after the Udyam lookup.
 */
export const GATE_HIDES = {
  countryOrigin: ['emailVerified', 'gstinVerified'],
  bankVerified: [
    'bankName',
    'branchName',
    'accountNumber',
    'confirmAccountNumber',
    'accountType',
    'ifscCode',
    'swiftCode',
    'bankAddress',
    'bankProof',
  ],
  udyamVerified: ['msmeCategory', 'msmeNumber', 'classificationYear', 'classificationDate', 'vendorType', 'msmeCertificate'],
};

/** The DOM id a form key renders under: "companyName" → "field-companyName", "agreements.nda" → "field-agreements-nda". */
export function fieldDomId(key) {
  return `field-${String(key).replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

/** Spread onto the element that stands for a form key, so the page can scroll to it and read the order fields appear in. */
export function fieldProps(key) {
  return { id: fieldDomId(key), 'data-field': key };
}

const keepsCapitals = (word) => /\d/.test(word) || (word.match(/[A-Z]/g) || []).length >= 2;

/** "Name of the Director" → "Name of the director". Words with two capitals or a digit (SPOC, GSTIN, 4M) keep them. */
export function sentenceCase(label) {
  return String(label ?? '')
    .split(' ')
    .map((word, i) => (i === 0 || keepsCapitals(word) ? word : word.charAt(0).toLowerCase() + word.slice(1)))
    .join(' ');
}

/** Drops the errors whose inputs a pending gate is hiding. The gate's own error always stays. */
export function withoutGatedFields(errors, hides = GATE_HIDES) {
  const all = errors || {};
  const hidden = new Set(Object.keys(all).flatMap((k) => hides[k] || []));
  if (hidden.size === 0) return all;
  return Object.fromEntries(Object.entries(all).filter(([k]) => !hidden.has(k)));
}

/**
 * Error keys in the order a vendor meets them: keys found in `orderedKeys` (the page, top to
 * bottom) follow that order. Keys the page does not render come first — on the Review step those
 * belong to earlier sections, which read before anything on the page itself.
 */
export function orderErrorKeys(errors, orderedKeys = []) {
  const keys = Object.keys(errors || {});
  const rank = new Map();
  orderedKeys.forEach((k, i) => {
    if (!rank.has(k)) rank.set(k, i);
  });
  const offPage = keys.filter((k) => !rank.has(k));
  const onPage = keys.filter((k) => rank.has(k)).sort((a, b) => rank.get(a) - rank.get(b));
  return [...offPage, ...onPage];
}

/** The errors a vendor can act on right now, as an object in on-screen order. */
export function actionableErrors(errors, orderedKeys = [], hides = GATE_HIDES) {
  const visible = withoutGatedFields(errors, hides);
  return Object.fromEntries(orderErrorKeys(visible, orderedKeys).map((k) => [k, visible[k]]));
}

/** The field to take the vendor to first, or null when nothing is missing. */
export function firstErrorField(errors, orderedKeys = []) {
  return orderErrorKeys(errors, orderedKeys)[0] ?? null;
}

/**
 * "Still needed: Digital signature, Director email +2 more" — the first `max` names, in the
 * order the errors are given, and a count of the rest.
 */
export function missingFieldSummary(errors, labels, max = 2) {
  const keys = Object.keys(errors || {});
  const shownKeys = keys.slice(0, Math.max(1, max));
  const names = shownKeys.map((k) => sentenceCase(fieldLabel(k, labels)));
  const more = keys.length - shownKeys.length;
  const text = keys.length === 0 ? '' : `Still needed: ${names.join(', ')}${more > 0 ? ` +${more} more` : ''}`;
  return { keys: keys.length === 0 ? [] : shownKeys, names: keys.length === 0 ? [] : names, more: Math.max(0, more), text };
}

/**
 * What the navigation bar says while a step is blocked, or null when it is not.
 *  { kind: 'gate', key, text }                 — only verification gates remain
 *  { kind: 'fields', keys, names, more, text } — anything else
 */
export function proceedHint(errors, { labels, hints = GATE_HINTS, max = 2 } = {}) {
  const keys = Object.keys(errors || {});
  if (keys.length === 0) return null;
  if (keys.every((k) => hints[k])) {
    if (keys.includes('gstinVerified') && keys.includes('emailVerified')) {
      return { kind: 'gate', key: 'gstinVerified', text: 'Verify your GSTIN and email to continue' };
    }
    // Nothing else on the Identity step shows until a country is picked, so the country comes first.
    const key = keys.includes('countryOrigin') ? 'countryOrigin' : keys[0];
    return { kind: 'gate', key, text: hints[key] };
  }
  return { kind: 'fields', ...missingFieldSummary(errors, labels, max) };
}
